import { useEffect, useState } from "react";
import { getSocket } from "@/lib/socket";
import type { ScoreboardState } from "@/types";

// Réplica compacta del marcador real de la app puntopadel (Flutter, lib/app/ui/pages/inicio/
// widgets/marcador/*.dart): tarjeta oscura #353333 con borde blanco, una fila por equipo con
// una caja de "juego" (azul/morado tiebreak/dorado punto de oro) + 3 cajas de sets
// (verde si ganado, naranja si perdido, gris si en curso/no jugado todavía).

type GameKind = "game" | "tiebreak" | "golden";
type SetTone = "winner" | "loser" | "pending";

const GAME_GRADIENT: Record<GameKind, string> = {
  game: "linear-gradient(100deg, #2565f9, #98a1f4)",
  tiebreak: "linear-gradient(100deg, #b625f9, #dc98f4)",
  golden: "linear-gradient(100deg, #f9c225, #f4dc98)",
};

const SET_GRADIENT: Record<SetTone, string> = {
  winner: "linear-gradient(100deg, #3af925, #98f498)",
  loser: "linear-gradient(100deg, #f46325, #fb986d)",
  pending: "linear-gradient(100deg, #c4cac1, #a2a7a2)",
};

const GAME_LABEL: Record<GameKind, string> = {
  game: "Juego",
  tiebreak: "Tiebreak",
  golden: "P. de oro",
};

type ScoreboardSize = "sm" | "lg";

const SIZE_CLASSES = {
  sm: {
    box: "h-6 w-6 text-[11px] sm:h-7 sm:w-7 sm:text-xs",
    label: "w-14 text-[10px] sm:text-[11px]",
    dot: "h-1.5 w-1.5",
    header: "text-[8px] sm:text-[9px]",
    headerPad: "pl-[3.9rem]",
    setCol: "w-6 sm:w-7",
    card: "gap-1.5 p-2",
  },
  lg: {
    box: "h-10 w-10 text-base sm:h-12 sm:w-12 sm:text-lg",
    label: "w-20 text-sm sm:w-24 sm:text-base",
    dot: "h-2.5 w-2.5",
    header: "text-xs sm:text-sm",
    headerPad: "pl-24 sm:pl-28",
    setCol: "w-10 sm:w-12",
    card: "gap-3 p-4",
  },
} as const;

function ScoreBox({ value, gradient, size }: { value: number | null; gradient: string; size: ScoreboardSize }) {
  return (
    <div
      className={`flex items-center justify-center rounded-[3px] font-bold text-[#090f13] ${SIZE_CLASSES[size].box}`}
      style={{ background: gradient }}
    >
      {value ?? ""}
    </div>
  );
}

function TeamRow({
  label,
  serving,
  gameValue,
  gameKind,
  sets,
  size,
}: {
  label: string;
  serving: boolean;
  gameValue: number;
  gameKind: GameKind;
  sets: { value: number | null; tone: SetTone }[];
  size: ScoreboardSize;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`flex shrink-0 items-center gap-1 truncate font-semibold text-white ${SIZE_CLASSES[size].label}`}>
        {serving && <span className={`shrink-0 rounded-full bg-brand-400 ${SIZE_CLASSES[size].dot}`} />}
        {label}
      </span>
      <ScoreBox value={gameValue} gradient={GAME_GRADIENT[gameKind]} size={size} />
      {sets.map((set, i) => (
        <ScoreBox key={i} value={set.value} gradient={SET_GRADIENT[set.tone]} size={size} />
      ))}
    </div>
  );
}

export function ScoreboardOverlay({ courtId, initial }: { courtId: number; initial?: ScoreboardState | null }) {
  const [score, setScore] = useState<ScoreboardState | null>(initial ?? null);

  // El marcador solo emite eventos puntuales: si el partido ya estaba en curso al cargar
  // la página, `initial` (servido por GET /matches/live) es lo único que nos da el estado
  // actual hasta que llegue el siguiente punto en vivo.
  useEffect(() => {
    setScore(initial ?? null);
  }, [courtId, initial]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    function onUpdate(payload: { courtId: number; data: ScoreboardState }) {
      if (payload.courtId === courtId) setScore(payload.data);
    }

    socket.on("scoreboard:update", onUpdate);
    return () => {
      socket.off("scoreboard:update", onUpdate);
    };
  }, [courtId]);

  return <ScoreboardDisplay score={score} />;
}

// Presentacional puro, sin opinión sobre cómo llega el estado — ScoreboardOverlay lo alimenta
// por Socket.IO (sesión de usuario logueado); la vista de dispositivo (tablet/pantalla sin
// login, ver DeviceViewPage/RefereePage) lo alimenta por polling normal.
// size="sm" (por defecto): overlay flotando encima de un vídeo, posicionado en absoluto.
// size="lg": bloque normal dentro del flujo de la página (nada de position:absolute — si no,
// no reserva espacio real y se monta encima de lo que tenga al lado).
export function ScoreboardDisplay({ score, size = "sm" }: { score: ScoreboardState | null; size?: ScoreboardSize }) {
  if (!score) return null;
  const positionClass = size === "sm" ? "absolute top-3 left-3" : "";

  if (score.estado === "ganador") {
    const winner = score.winAllGameTeam === "b" ? "Equipo B" : "Equipo A";
    return (
      <div
        className={`rounded-md border-2 border-white bg-[#353333] font-bold text-white shadow-lg ${positionClass} ${
          size === "lg" ? "px-6 py-4 text-xl" : "px-3 py-1.5 text-xs"
        }`}
      >
        🏆 Ganador: {winner}
      </div>
    );
  }

  const currentSet = score.set_juego;
  const gamesA = Number(score[`set_${currentSet}_a`] ?? 0);
  const gamesB = Number(score[`set_${currentSet}_b`] ?? 0);
  const isGolden = score.equipo_a === 40 && score.equipo_b === 40;
  const isTiebreak = gamesA === 6 && gamesB === 6;
  const gameKind: GameKind = isGolden ? "golden" : isTiebreak ? "tiebreak" : "game";
  const servingTeam = score.saque && score.saque > 2 ? "b" : "a";

  function setsFor(team: "a" | "b") {
    return [1, 2, 3].map((setNumber) => {
      const teamGames = score![`set_${setNumber}_${team}`];
      const oppGames = score![`set_${setNumber}_${team === "a" ? "b" : "a"}`];
      if (setNumber > currentSet || teamGames == null) {
        return { value: null, tone: "pending" as SetTone };
      }
      if (setNumber === currentSet) {
        return { value: Number(teamGames), tone: "pending" as SetTone };
      }
      return { value: Number(teamGames), tone: Number(teamGames) > Number(oppGames) ? "winner" : ("loser" as SetTone) };
    });
  }

  const s = SIZE_CLASSES[size];
  return (
    <div className={`flex flex-col rounded-md border-2 border-white bg-[#353333] shadow-lg ${positionClass} ${s.card}`}>
      <div className={`flex items-center justify-between gap-2 font-bold tracking-wide text-white/70 uppercase ${s.header} ${s.headerPad}`}>
        <span>{GAME_LABEL[gameKind]}</span>
        <span className="flex gap-1.5 pr-1">
          <span className={`text-center ${s.setCol}`}>Set 1</span>
          <span className={`text-center ${s.setCol}`}>Set 2</span>
          <span className={`text-center ${s.setCol}`}>Set 3</span>
        </span>
      </div>
      <TeamRow
        label="Equipo A"
        serving={servingTeam === "a"}
        gameValue={score.equipo_a}
        gameKind={gameKind}
        sets={setsFor("a")}
        size={size}
      />
      <TeamRow
        label="Equipo B"
        serving={servingTeam === "b"}
        gameValue={score.equipo_b}
        gameKind={gameKind}
        sets={setsFor("b")}
        size={size}
      />
    </div>
  );
}
